import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { useCartUI } from '../../contexts/CartUIContext';
import { useSettings } from '../../contexts/SettingsContext';
import { useAuth } from '../../contexts/AuthContext';

const CartDrawer = () => {
  const { isCartOpen, closeCart } = useCartUI();
  const {
    items, totalItems, subtotalUSD, subtotalBS, deliveryFeeUSD, deliveryFeeBS,
    totalUSD, totalBS, exchangeRate, isWholesale, getItemPrice,
    removeItem, updateQuantity, clearCart
  } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleCheckout = () => {
    if (!isAuthenticated) {
      closeCart();
      navigate('/login?redirect=/checkout');
      return;
    }
    closeCart();
    navigate('/checkout');
  };

  const handleGoToProducts = () => {
    closeCart();
    navigate('/productos');
  };

  React.useEffect(() => {
    if (isCartOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isCartOpen]);

  React.useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isCartOpen) closeCart();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isCartOpen, closeCart]);

  if (!isCartOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex justify-end">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={closeCart} />

      <div className="relative w-full sm:max-w-md bg-white h-full shadow-2xl flex flex-col animate-slide-in">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-primary/5 bg-background">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🛒</span>
            <h2 className="text-lg font-serif font-bold">
              Tu pedido {totalItems > 0 && <span className="text-gold">({totalItems})</span>}
            </h2>
          </div>
          <button
            onClick={closeCart}
            className="text-2xl hover:text-gold transition p-2 rounded-full hover:bg-gold/5"
          >
            ✕
          </button>
        </div>

        {/* Banner mayorista */}
        {isWholesale && (
          <div className="bg-green-50 border-b border-green-200 px-4 py-2 text-xs text-green-800 text-center font-medium">
            🏪 Modo Mayorista - Precios especiales aplicados
          </div>
        )}

        {/* Items */}
        <div className="flex-1 overflow-y-auto p-4">
          {items.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-7xl mb-4">🛒</div>
              <p className="text-lg font-semibold text-primary mb-2">Tu carrito está vacío</p>
              <p className="text-sm text-text-secondary mb-6">Agrega productos para continuar</p>
              <button onClick={handleGoToProducts} className="btn-primary inline-flex">
                🍕 Ver productos
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map(item => {
                const appliedPrice = getItemPrice(item);
                const isWholesaleApplied = appliedPrice === item.priceWholesale && item.priceWholesale;

                return (
                  <div key={item.id} className="flex gap-3 p-3 bg-background-alt/50 rounded-xl">
                    <div className="w-14 h-14 rounded-lg bg-white flex items-center justify-center text-3xl flex-shrink-0 shadow-sm">
                      {item.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-primary truncate flex items-center gap-1">
                            {item.name}
                            {isWholesaleApplied && (
                              <span className="text-[10px] bg-green-500 text-white px-1.5 py-0.5 rounded font-bold">
                                MAYOR
                              </span>
                            )}
                          </div>
                          <div className="text-gold font-bold text-sm mt-0.5">
                            ${appliedPrice.toFixed(2)}
                            {isWholesaleApplied && (
                              <span className="text-xs text-gray-400 line-through ml-2">
                                ${item.price.toFixed(2)}
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => removeItem(item.id)}
                          className="text-gray-400 hover:text-red-500 transition flex-shrink-0 p-1"
                        >
                          ✕
                        </button>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-2 bg-white rounded-full border border-gray-200">
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="w-7 h-7 rounded-full hover:bg-gold hover:text-white transition flex items-center justify-center font-bold"
                          >
                            −
                          </button>
                          <span className="w-6 text-center font-semibold text-sm">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="w-7 h-7 rounded-full hover:bg-gold hover:text-white transition flex items-center justify-center font-bold"
                          >
                            +
                          </button>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-primary text-sm">
                            ${(appliedPrice * item.quantity).toFixed(2)}
                          </div>
                          <div className="text-xs text-text-light">
                            Bs {(appliedPrice * item.quantity * exchangeRate).toFixed(2)}
                          </div>
                        </div>
                      </div>

                      {/* Aviso para alcanzar precio mayorista */}
                      {isWholesale && item.priceWholesale && item.quantity < item.minWholesaleQty && (
                        <div className="mt-2 text-xs text-orange-600 bg-orange-50 rounded px-2 py-1">
                          ⚠️ Agrega {item.minWholesaleQty - item.quantity} más para precio mayorista
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-primary/5 bg-background p-4 space-y-3">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal</span>
                <div className="text-right">
                  <div className="font-medium">${subtotalUSD.toFixed(2)}</div>
                  <div className="text-xs text-gray-400">Bs {subtotalBS.toFixed(2)}</div>
                </div>
              </div>
              {deliveryFeeUSD > 0 && (
                <div className="flex justify-between text-gray-600">
                  <span>Envío</span>
                  <div className="text-right">
                    <div className="font-medium">${deliveryFeeUSD.toFixed(2)}</div>
                    <div className="text-xs text-gray-400">Bs {deliveryFeeBS.toFixed(2)}</div>
                  </div>
                </div>
              )}
              <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                <span className="font-bold text-base">Total</span>
                <div className="text-right">
                  <div className="text-xl font-bold text-gold">${totalUSD.toFixed(2)}</div>
                  <div className="text-xs text-gray-500">Bs {totalBS.toFixed(2)}</div>
                </div>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <button
                onClick={clearCart}
                className="py-3 px-4 border border-red-200 text-red-500 rounded-full hover:bg-red-50 transition font-medium text-sm"
              >
                🗑️
              </button>
              <button
                onClick={handleCheckout}
                className="flex-1 py-3 bg-gold text-white rounded-full hover:bg-gold-dark transition font-semibold shadow-md"
              >
                {isAuthenticated ? 'Ir a pagar →' : 'Iniciar sesión para pagar →'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartDrawer;
